import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Flex, Heading, SegmentedControl, Table, Text } from '@radix-ui/themes'
import { useMemo, useState } from 'react'
import { Ops } from '@willie/schemas'
import { useAuth } from '~/auth/AuthProvider'
import { useTRPC } from '~/utils/trpc'
import {
  STALLED_BECAUSE_OF_OPTIONS,
  stalledBecauseOfLabel,
  type StalledBecauseOf
} from '~/features/installations/constants'
import { daysStalledColor, integerDaysSince } from '~/features/installations/overview'

type StalledInstallation = Ops.StationInstallations.StalledInstallation
type StalledFilter = 'all' | 'mine' | 'unassigned'
type StalledCauseFilter = 'all' | StalledBecauseOf

const StalledTable = ({
  items,
  onRowClick
}: {
  items: StalledInstallation[]
  onRowClick: (id: number) => void
}) => {
  if (items.length === 0) {
    return (
      <Box py="6">
        <Text color="gray">Nothing stalled here — everything is moving.</Text>
      </Box>
    )
  }

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Installation</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Organization</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Site</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Cause</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Assigned to</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Stalled</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {items.map((row) => {
          const days = integerDaysSince(row.stalledAt)
          const assignee = Ops.StationInstallations.assigneeDisplayName(row)
          return (
            <Table.Row
              key={row.installationId}
              onClick={() => onRowClick(row.installationId)}
              className="cursor-pointer hover:bg-[var(--gray-a2)]"
            >
              <Table.Cell>
                <Flex direction="column">
                  <Text size="1" className="font-mono text-[var(--accent-11)]">
                    #{row.installationId}
                  </Text>
                  <Text size="2">{row.name ?? '—'}</Text>
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{row.orgName ?? '—'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{row.siteName ?? '—'}</Text>
              </Table.Cell>
              <Table.Cell>
                <Badge color="gray" variant="soft" size="1">
                  {stalledBecauseOfLabel[row.stalledBecauseOf]}
                </Badge>
              </Table.Cell>
              <Table.Cell>
                {assignee ? (
                  <Text size="2">{assignee}</Text>
                ) : (
                  <Text size="2" color="gray">
                    Unassigned
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell>
                <Text size="2" className="line-clamp-2">
                  {row.note}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text weight="medium" color={daysStalledColor(days)}>
                  {days}d
                </Text>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}

const StalledPage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const { session } = useAuth()
  const stalledQuery = useQuery(trpc.ops.stationInstallations.listStalled.queryOptions())
  const [filter, setFilter] = useState<StalledFilter>('all')
  const [causeFilter, setCauseFilter] = useState<StalledCauseFilter>('all')

  const currentUserId = session?.user.id ?? null
  const all = stalledQuery.data ?? []

  const items = useMemo(() => {
    const matchesAssignment = (row: StalledInstallation) => {
      if (filter === 'mine') return row.assignedToUserId === currentUserId
      if (filter === 'unassigned') return row.assignedToUserId === null
      return true
    }
    const matchesCause = (row: StalledInstallation) =>
      causeFilter === 'all' || row.stalledBecauseOf === causeFilter
    return all.filter((row) => matchesAssignment(row) && matchesCause(row))
  }, [all, filter, causeFilter, currentUserId])

  return (
    <Flex direction="column" gap="4">
      <Flex align="center" gap="3">
        <Heading size="6">Stalled installations</Heading>
        <Badge color="red" variant="soft">
          {items.length}
        </Badge>
      </Flex>

      <Flex direction="column" align="start" gap="2">
        <SegmentedControl.Root value={filter} onValueChange={(v) => setFilter(v as StalledFilter)} size="1">
          <SegmentedControl.Item value="all">All</SegmentedControl.Item>
          <SegmentedControl.Item value="mine">Only mine</SegmentedControl.Item>
          <SegmentedControl.Item value="unassigned">Unassigned</SegmentedControl.Item>
        </SegmentedControl.Root>
        <SegmentedControl.Root
          value={causeFilter}
          onValueChange={(v) => setCauseFilter(v as StalledCauseFilter)}
          size="1"
        >
          <SegmentedControl.Item value="all">All causes</SegmentedControl.Item>
          {STALLED_BECAUSE_OF_OPTIONS.map((option) => (
            <SegmentedControl.Item key={option} value={option}>
              {stalledBecauseOfLabel[option]}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
      </Flex>

      {stalledQuery.isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : stalledQuery.error ? (
        <Text color="red">{stalledQuery.error.message}</Text>
      ) : (
        <StalledTable
          items={items}
          onRowClick={(id) => navigate({ to: '/installations/$installationId', params: { installationId: String(id) } })}
        />
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/stalled/')({
  component: StalledPage
})
